import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, List, ListItemButton,
  CircularProgress, Alert, Button, Snackbar, Select, MenuItem, FormControl, InputLabel,
  TextField, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';
import DomainObjectModal from './DomainObjectModal';
import useDebounce from '../../hooks/useDebounce';
import DefaultDomainObjectListItemContent from './DefaultDomainObjectListItemContent';
import { useResourceState, useResourceDispatch, useResources } from '../../context/ResourceContext';
import { useDates } from '../../context/DateContext';
import { useUI } from '../../context/UIContext';
import * as favoritesApi from '../../services/favoritesApi';
import ScrollIndicatorWrapper from '../common/ScrollIndicatorWrapper';

const MOVE_THRESHOLD = 10; // Pixels to move before a tap becomes a scroll

const DomainObjectManager = ({
  config,
  sectionTitle,
  onAdd,
  hideAddButton = false,
  initialDisplayLimit,
  customFilter
}) => {
  const {
    pluralName,
    singularName,
    api,
    getDisplayName,
    getAdditionalProps,
    sortOptions,
    searchable,
    searchableThreshold = 10,
    sortable = false,
    sortableThreshold = 10,
    searchFilter,
    enableFavorites,
    favoriteResourceType,
    defaultToFavorites,
  } = config;

  const { getResourcesByType, isLoading: isContextLoading, error: contextError, refreshKey } = useResourceState();
  const { refreshResources } = useResourceDispatch();
  const { upsertResource } = useResources();
  const { dates: allDates } = useDates();
  const { openResourceTab } = useUI();

  const resourceType = config.resourceType || config.singularName.toLowerCase().replace(/ /g, '_');
  const backendFavType = favoriteResourceType || resourceType;
  const itemsFromContext = getResourcesByType(resourceType);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [initialMode, setInitialMode] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [additionalProps, setAdditionalProps] = useState({});
  const [isLoadingAdditionalProps, setIsLoadingAdditionalProps] = useState(false);
  const [additionalPropsError, setAdditionalPropsError] = useState(null);
  const [sortValue, setSortValue] = useState(sortOptions?.[0]?.value || 'default');
  const [searchQuery, setSearchQuery] = useState('');
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);

  const isFirstLoad = useRef(true);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Refs for scroll/touch protection
  const longPressTimer = useRef();
  const wasLongPress = useRef(false);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isMoving = useRef(false);

  const addButtonLabel = config.addButtonLabel || `Add ${singularName}`;

  useEffect(() => {
    if (enableFavorites && backendFavType) {
      if (isFirstLoad.current) {
        setIsLoadingFavorites(true);
      }
      favoritesApi.getFavoritesByType(backendFavType)
        .then(ids => {
          setFavoriteIds(new Set(ids));
        })
        .catch(err => console.error("Failed to load favorites", err))
        .finally(() => {
          setIsLoadingFavorites(false);
          isFirstLoad.current = false;
        });
    }
  }, [enableFavorites, backendFavType, refreshKey]);

  const handleToggleFavorite = useCallback(async (e, item) => {
    e.stopPropagation();
    const id = item.id;
    const isCurrentlyFavorite = favoriteIds.has(id);
    setFavoriteIds(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyFavorite) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });

    try {
      if (isCurrentlyFavorite) {
        await favoritesApi.removeFavorite(backendFavType, id);
      } else {
        await favoritesApi.addFavorite(backendFavType, id);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      setFavoriteIds(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFavorite) newSet.add(id);
        else newSet.delete(id);
        return newSet;
      });
      setSnackbarMessage("Failed to update favorite status.");
    }
  }, [favoriteIds, backendFavType]);

  const fetchAdditionalProps = useCallback(async () => {
    if (getAdditionalProps) {
      try {
        setIsLoadingAdditionalProps(true);
        setAdditionalPropsError(null);
        const extraProps = await getAdditionalProps();
        setAdditionalProps(extraProps);
      } catch (err) {
        setAdditionalPropsError(err.message || 'Failed to load supporting data.');
        console.error('Failed to fetch additional props:', err);
      } finally {
        setIsLoadingAdditionalProps(false);
      }
    }
  }, [getAdditionalProps]);

  useEffect(() => {
    fetchAdditionalProps();
  }, [fetchAdditionalProps]);

  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setInitialMode('edit');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setInitialMode('view');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenAsTab = useCallback((item) => {
    const type = config.resourceType || config.singularName.toLowerCase().replace(/ /g, '_');
    openResourceTab({
      ...item,
      type: type,
      resource_type: type
    });
  }, [config, openResourceTab]);

  /**
   * Touch/Mouse Start: Initialize position and start long-press timer
   */
  const handlePressStart = useCallback((e, item) => {
    isMoving.current = false;
    wasLongPress.current = false;

    // Track starting position for scroll detection
    if (e.touches && e.touches.length > 0) {
      touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else {
      touchStartPos.current = { x: e.clientX, y: e.clientY };
    }

    longPressTimer.current = setTimeout(() => {
      if (!isMoving.current) {
        wasLongPress.current = true;
        handleOpenAsTab(item);
      }
    }, 700);
  }, [handleOpenAsTab]);

  /**
   * Touch Move: Detect if user is scrolling
   */
  const handleTouchMove = useCallback((e) => {
    if (isMoving.current) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;

    const dx = Math.abs(currentX - touchStartPos.current.x);
    const dy = Math.abs(currentY - touchStartPos.current.y);

    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      isMoving.current = true;
      clearTimeout(longPressTimer.current);
    }
  }, []);

  const handlePressEnd = useCallback(() => {
    clearTimeout(longPressTimer.current);
  }, []);

  /**
   * Primary click handler: Only triggers if no scrolling or long-press occurred
   */
  const handleItemClick = (item) => {
    if (isMoving.current || wasLongPress.current) return;
    handleOpenEditModal(item);
  };

  const handleItemDoubleClick = (item) => {
    clearTimeout(longPressTimer.current);
    handleOpenAsTab(item);
  };

  const handleModalSuccess = useCallback((action, item) => {
    if (action === 'delete') {
      refreshResources();
      setIsModalOpen(false);
      setEditingItem(null);
      setSnackbarMessage(`${singularName} deleted successfully!`);
    } else if (action === 'revert') {
      refreshResources();
      setIsModalOpen(false);
      setEditingItem(null);
      setSnackbarMessage(`${singularName} reverted to system default.`);
    } else if (action === 'create_and_view' || action === 'update_and_view') {
      if (item) {
        upsertResource(item);
      }
      setEditingItem(item);
      setInitialMode('view');
      setSnackbarMessage(`${singularName} ${action.includes('create') ? 'created' : 'saved'} successfully!`);
    } else {
      if (item) {
        upsertResource(item);
      }
      if ((config.id === 'workoutLogs' || config.createChainToEdit) && action === 'create') {
        setEditingItem(item);
        setInitialMode('edit');
      }
    }
  }, [refreshResources, upsertResource, singularName, config.id, config.createChainToEdit]);

  const listBeforeSearch = useMemo(() => {
    let items = itemsFromContext;
    if (customFilter) {
      items = customFilter(items);
    }
    if (defaultToFavorites && enableFavorites) {
      if (isLoadingFavorites && isFirstLoad.current) {
        return [];
      }
      items = items.filter(item => favoriteIds.has(item.id));
    }
    return items;
  }, [itemsFromContext, defaultToFavorites, enableFavorites, isLoadingFavorites, favoriteIds, customFilter]);

  const filteredAndSortedItems = useMemo(() => {
    let itemsToProcess = listBeforeSearch;
    if (debouncedSearchQuery.trim()) {
      const lowercasedQuery = debouncedSearchQuery.toLowerCase();
      const filterFn = searchFilter || ((item, query) => {
        const nameMatch = getDisplayName(item)?.toLowerCase().includes(lowercasedQuery);
        const descriptionMatch = item.description?.toLowerCase().includes(lowercasedQuery);
        return nameMatch || descriptionMatch;
      });
      itemsToProcess = itemsToProcess.filter(item => filterFn(item, lowercasedQuery, additionalProps));
    }

    const itemsCopy = [...itemsToProcess];
    const allSorterProps = { ...additionalProps, allDates };
    const selectedSorter = sortOptions?.find(opt => opt.value === sortValue)?.sorter;

    if (selectedSorter) {
      return itemsCopy.sort((a, b) => selectedSorter(a, b, allSorterProps));
    }
    return itemsCopy.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
  }, [
    listBeforeSearch,
    debouncedSearchQuery,
    searchFilter,
    getDisplayName,
    additionalProps,
    sortValue,
    sortOptions,
    allDates,
  ]);

  const listContainerSx = useMemo(() => {
    const baseSx = {
      width: '100%',
      bgcolor: 'background.paper',
      borderRadius: 1,
      p: 0,
    };
    if (initialDisplayLimit && filteredAndSortedItems.length > initialDisplayLimit) {
      const itemHeight = 40;
      baseSx.maxHeight = `${initialDisplayLimit * itemHeight}px`;
      baseSx.height = 'auto';
    }
    return baseSx;
  }, [initialDisplayLimit, filteredAndSortedItems.length]);

  const isLoading = isContextLoading && itemsFromContext.length === 0;
  const error = contextError || additionalPropsError;
  const ListItemContent = config.renderListItemContent || DefaultDomainObjectListItemContent;

  const showSearch = searchable && listBeforeSearch.length > searchableThreshold;
  const showSort = sortable && (sortOptions && sortOptions.length > 1 && listBeforeSearch.length > sortableThreshold);
  const placeholderText = (defaultToFavorites && enableFavorites) ? `Search Favorites...` : `Search ${pluralName}...`;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 1 }}>Loading {pluralName.toLowerCase()}...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 1, fontSize: '0.8rem' }}>{`Error: ${error}`}</Alert>;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!hideAddButton && (
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd || handleOpenCreateModal}
            size="small"
          >
            {addButtonLabel}
          </Button>
        </Box>
      )}

      {(showSearch || showSort) && (
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showSearch && (
            <TextField
              fullWidth
              size="small"
              placeholder={placeholderText}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {showSort && (
            <FormControl fullWidth size="small">
              <InputLabel id={`${config.id}-sort-label`}>Sort By</InputLabel>
              <Select
                labelId={`${config.id}-sort-label`}
                id={`${config.id}-sort-select`}
                value={sortValue}
                label="Sort By"
                onChange={(e) => setSortValue(e.target.value)}
              >
                {sortOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {filteredAndSortedItems.length === 0 && !isLoading ? (
        <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          {searchQuery.trim() ? `No results found for "${searchQuery}".` : `No ${pluralName.toLowerCase()} found. Add your first one!`}
        </Typography>
      ) : (
        <ScrollIndicatorWrapper
          containerSx={{ flexGrow: 1 }}
          sx={listContainerSx}
        >
          <List component="nav" dense disablePadding>
            {filteredAndSortedItems.map((item) => {
              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  onDoubleClick={() => handleItemDoubleClick(item)}
                  onMouseDown={(e) => handlePressStart(e, item)}
                  onMouseUp={handlePressEnd}
                  onMouseLeave={handlePressEnd}
                  onTouchStart={(e) => handlePressStart(e, item)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handlePressEnd}
                  sx={{ py: 0.75, minHeight: '40px' }}
                >
                  <ListItemContent
                    item={item}
                    config={config}
                    isFavorite={enableFavorites ? favoriteIds.has(item.id) : false}
                    onToggleFavorite={enableFavorites ? (e) => handleToggleFavorite(e, item) : undefined}
                    {...additionalProps}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </ScrollIndicatorWrapper>
      )}

      {isModalOpen && (
        <DomainObjectModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onActionSuccess={handleModalSuccess}
          onContainedItemCreated={fetchAdditionalProps}
          itemToEdit={editingItem}
          initialMode={initialMode}
          config={config}
          sectionTitle={sectionTitle}
          {...additionalProps}
        />
      )}

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage('')}
        message={<Box sx={{ display: 'flex', alignItems: 'center' }}><CheckCircleIcon sx={{ mr: 1 }} />{snackbarMessage}</Box>}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

DomainObjectManager.propTypes = {
  config: PropTypes.object.isRequired,
  sectionTitle: PropTypes.string,
  onAdd: PropTypes.func,
  hideAddButton: PropTypes.bool,
  initialDisplayLimit: PropTypes.number,
  customFilter: PropTypes.func,
};

export default DomainObjectManager;